import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import s from './Page.module.css';
import { ITENS_SIMPLES, GRUPOS } from '../config/menus';

// Catálogo unificado: itens simples viram grupos sem filhos
const CATALOGO = [
  ...ITENS_SIMPLES.map(i => ({ id: i.id, label: i.label, icon: i.icon, filhos: [] })),
  ...GRUPOS.map(g => ({ id: g.id, label: g.label, icon: g.icon, filhos: g.filhos })),
];

const FORM_VAZIO = { id: null, nome: '', descricao: '', sistema: false, menus: [], submenus: [], libera_senha_admin: false };

const box = {
  card:      { border: '1px solid var(--border-color, #d8dee6)', borderRadius: 6, padding: '10px 12px', background: 'var(--bg-card, #fff)' },
  menuRow:   { display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13 },
  subWrap:   { margin: '6px 0 2px 24px', display: 'flex', flexDirection: 'column', gap: 4 },
  subRow:    { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 },
  grade:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10, marginTop: 12 },
};

export default function PerfisUsuario() {
  const [lista, setLista]           = useState([]);
  const [form, setForm]             = useState(FORM_VAZIO);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [salvando, setSalvando]     = useState(false);
  const [erro, setErro]             = useState('');
  const [ok, setOk]                 = useState('');

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get('/perfis');
      setLista(data);
    } catch { setErro('Erro ao carregar perfis.'); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const novo = () => {
    setForm(FORM_VAZIO);
    setErro(''); setOk('');
    setMostrarForm(true);
  };

  const editar = (p) => {
    const perm = p.permissoes || {};
    const tudo = p.sistema || (perm.menus || []).includes('*');
    setForm({
      id: p.id,
      nome: p.nome,
      descricao: p.descricao || '',
      sistema: p.sistema,
      menus:    tudo ? CATALOGO.map(m => m.id) : (perm.menus || []),
      submenus: tudo ? CATALOGO.flatMap(m => m.filhos.map(f => f.to)) : (perm.submenus || []),
      libera_senha_admin: !!p.libera_senha_admin,
    });
    setErro(''); setOk('');
    setMostrarForm(true);
  };

  const fechar = () => { setMostrarForm(false); setForm(FORM_VAZIO); setErro(''); setOk(''); };

  // Marcar menu marca todos os submenus; desmarcar limpa os submenus dele
  const toggleMenu = (menu) => {
    setForm(f => {
      const marcado = f.menus.includes(menu.id);
      if (marcado) {
        return {
          ...f,
          menus:    f.menus.filter(m => m !== menu.id),
          submenus: f.submenus.filter(sm => !menu.filhos.some(fi => fi.to === sm)),
        };
      }
      return {
        ...f,
        menus:    [...f.menus, menu.id],
        submenus: [...f.submenus, ...menu.filhos.map(fi => fi.to).filter(to => !f.submenus.includes(to))],
      };
    });
  };

  const toggleSub = (to) => {
    setForm(f => ({
      ...f,
      submenus: f.submenus.includes(to) ? f.submenus.filter(x => x !== to) : [...f.submenus, to],
    }));
  };

  const salvar = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) { setErro('Informe o nome do perfil.'); return; }
    setSalvando(true); setErro(''); setOk('');
    try {
      const payload = {
        nome: form.nome.trim(),
        descricao: form.descricao.trim() || null,
        permissoes: { menus: form.menus, submenus: form.submenus },
        libera_senha_admin: form.libera_senha_admin,
      };
      if (form.id) await api.put(`/perfis/${form.id}`, payload);
      else {
        const { data } = await api.post('/perfis', payload);
        setForm(f => ({ ...f, id: data.id }));
      }
      setOk('Perfil salvo com sucesso!');
      carregar();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar perfil.');
    } finally { setSalvando(false); }
  };

  const excluir = async (p) => {
    if (!window.confirm(`Excluir o perfil "${p.nome}"?`)) return;
    try {
      await api.delete(`/perfis/${p.id}`);
      if (form.id === p.id) fechar();
      carregar();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao excluir.');
    }
  };

  const resumoPermissoes = (p) => {
    const base = (p.sistema || (p.permissoes?.menus || []).includes('*'))
      ? 'Acesso total'
      : ((p.permissoes?.menus || []).length ? `${(p.permissoes?.menus || []).length} menu(s)` : 'Nenhum acesso');
    return p.libera_senha_admin ? `${base} · libera admin` : base;
  };

  return (
    <div>
      <div className={s.topbar}>
        <h2 className={s.titulo}>Perfis de Usuário</h2>
        <div className={s.acoes}>
          {!mostrarForm && <button className={s.btnPrimario} onClick={novo}>+ Novo Perfil</button>}
          {mostrarForm  && <button className={s.btnSecundario} onClick={fechar}>Fechar</button>}
        </div>
      </div>

      {/* ── Formulário ── */}
      {mostrarForm && (
        <form onSubmit={salvar} style={{ ...box.card, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, fontWeight: 600, flex: '0 0 220px' }}>
              Nome do Perfil *
              <input value={form.nome} disabled={form.sistema}
                placeholder="Ex: Financeiro, Gerente..."
                style={{ padding: '6px 8px', fontSize: 13, border: '1px solid #ccc', borderRadius: 4 }}
                onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, fontWeight: 600, flex: 1, minWidth: 240 }}>
              Descrição
              <input value={form.descricao} disabled={form.sistema}
                placeholder="Para que serve este perfil (opcional)"
                style={{ padding: '6px 8px', fontSize: 13, border: '1px solid #ccc', borderRadius: 4 }}
                onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} />
            </label>
          </div>

          {form.sistema && (
            <p style={{ margin: '10px 0 0', fontSize: 12.5, color: '#92400e', background: '#fef3c7', padding: '6px 10px', borderRadius: 4 }}>
              🔒 O perfil <strong>Administrador</strong> é de sistema: vem com todos os menus marcados e não pode ser alterado nem excluído, por segurança.
            </p>
          )}

          {!form.sistema && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, marginTop: 12 }}>
              <input type="checkbox" checked={form.libera_senha_admin}
                onChange={e => setForm(f => ({ ...f, libera_senha_admin: e.target.checked }))} />
              A senha de usuários deste perfil também libera ações que pedem senha de administrador
              (cancelar venda, excluir cadastros, etc.)
            </label>
          )}

          {/* ── Grade de menus/submenus ── */}
          <div style={box.grade}>
            {CATALOGO.map(menu => {
              const menuMarcado = form.menus.includes(menu.id);
              return (
                <div key={menu.id} style={{ ...box.card, opacity: form.sistema ? 0.75 : 1 }}>
                  <label style={box.menuRow}>
                    <input type="checkbox"
                      checked={menuMarcado}
                      disabled={form.sistema}
                      onChange={() => toggleMenu(menu)} />
                    <span>{menu.icon}</span> {menu.label}
                  </label>
                  {menu.filhos.length > 0 && (
                    <div style={box.subWrap}>
                      {menu.filhos.map(f => (
                        <label key={f.to} style={{ ...box.subRow, color: menuMarcado ? 'inherit' : '#9aa3af' }}
                          title={menuMarcado ? f.to : 'Marque o menu principal para liberar os submenus'}>
                          <input type="checkbox"
                            checked={form.submenus.includes(f.to)}
                            disabled={form.sistema || !menuMarcado}
                            onChange={() => toggleSub(f.to)} />
                          {f.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {erro && <p style={{ color: '#b91c1c', fontSize: 13, marginTop: 10 }}>{erro}</p>}
          {ok   && <p style={{ color: '#15803d', fontSize: 13, marginTop: 10 }}>{ok}</p>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button type="button" className={s.btnSecundario} onClick={fechar}>Fechar</button>
            {!form.sistema && (
              <button type="submit" className={s.btnPrimario} disabled={salvando}>
                {salvando ? 'Salvando…' : form.id ? 'Atualizar' : 'Salvar'}
              </button>
            )}
          </div>
        </form>
      )}

      {/* ── Lista ── */}
      <div style={box.card}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ padding: '6px 8px' }}>Perfil</th>
              <th style={{ padding: '6px 8px' }}>Descrição</th>
              <th style={{ padding: '6px 8px', width: 130 }}>Acesso</th>
              <th style={{ padding: '6px 8px', width: 150 }}></th>
            </tr>
          </thead>
          <tbody>
            {lista.length === 0 && (
              <tr><td colSpan={4} style={{ padding: 14, textAlign: 'center', color: '#888' }}>Nenhum perfil cadastrado.</td></tr>
            )}
            {lista.map(p => (
              <tr key={p.id} style={{ borderBottom: '1px solid #eef1f5', background: form.id === p.id ? '#f0f7ff' : 'transparent' }}>
                <td style={{ padding: '6px 8px', fontWeight: 600 }}>
                  {p.nome}
                  {p.sistema && (
                    <span style={{ marginLeft: 8, fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: 4 }}>
                      🔒 sistema
                    </span>
                  )}
                </td>
                <td style={{ padding: '6px 8px', color: '#555' }}>{p.descricao || '—'}</td>
                <td style={{ padding: '6px 8px' }}>{resumoPermissoes(p)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                  <button className={s.btnSecundario} style={{ marginRight: 6 }} onClick={() => editar(p)}>
                    {p.sistema ? 'Visualizar' : 'Editar'}
                  </button>
                  {!p.sistema && (
                    <button className={s.btnPerigo} onClick={() => excluir(p)}>Excluir</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
